import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './CharacterSelection.css';
import './Travel.css';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import { authCardStyle, authButtonStyle } from '../constants/authStyles';
import { usePopupMessage } from '../hooks/usePopupMessage';
import { useCharacter } from '../context/CharacterContext';
import { useGameConfig } from '../context/GameConfigContext';
import { translateVillageName } from '../utils/translateGameData';
import Coachmark from './tutorial/Coachmark';

/** Seyahat sırasında giyili ekipmanıyla yolda yürüyen karakter. */
const TravelWalker = ({ characterId, username, progress, running, fromImg, toImg }) => {
  const [sheet, setSheet] = useState(null);

  useEffect(() => {
    if (!characterId || !username) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const eq = await axios.get(`/api/equipped-items?username=${encodeURIComponent(username)}`);
        const items = eq.data?.items || [];
        const weaponId = items.find((i) => i.slotType === 'weapon')?.baseItemId ?? 0;
        const armorId = items.find((i) => i.slotType === 'armor')?.baseItemId ?? 0;
        const res = await axios.get(
          `/api/battle/character-sprites?characterId=${characterId}&weaponId=${weaponId}&armorId=${armorId}`
        );
        const sp = res.data?.sprites;
        const s = sp?.normalSprite || sp?.allSprite;
        if (!cancelled && s?.url) setSheet(s);
      } catch (_) {
        // Görsel alınamazsa yalnızca yol çizgisi gösterilir.
      }
    })();
    return () => { cancelled = true; };
  }, [characterId, username]);

  const pct = Math.max(0, Math.min(1, progress));
  const url = sheet?.url ? (sheet.url.startsWith('/') ? sheet.url : `/${sheet.url}`) : '';
  const fc = sheet?.frameCount || 5;
  const rows = Math.max(1, Math.round((sheet?.sheetHeight || 300) / (sheet?.frameHeight || 100)));
  const row = sheet?.frameRow || 0;

  return (
    <div className={`travel-walk${running ? ' travel-walk--running' : ''}`} aria-hidden="true">
      {fromImg ? <img className="travel-walk__village travel-walk__village--from" src={fromImg} alt="" /> : null}
      {toImg ? <img className="travel-walk__village travel-walk__village--to" src={toImg} alt="" /> : null}
      <div className="travel-walk__road">
        <div className="travel-walk__road-done" style={{ width: `${pct * 100}%` }} />
      </div>
      <div className="travel-walk__hero" style={{ left: `calc(14% + ${pct * 72}%)` }}>
        {url ? (
          <span
            className="travel-walk__sprite"
            style={{
              backgroundImage: `url(${url})`,
              '--tw-fc': fc,
              '--tw-rows': rows,
              '--tw-row': row,
            }}
          />
        ) : (
          <span className="travel-walk__fallback">🚶</span>
        )}
        <span className="travel-walk__dust" />
      </div>
    </div>
  );
};

export const TravelPanel = () => {
  const { t: tg } = useTranslation('gameData');
  const { t: tc } = useTranslation('common');
  const { character: userCharacter, refreshCharacter } = useCharacter();
  const { gelistirmeConsole } = useGameConfig();
  const [villages, setVillages] = useState([]);
  const [selectedVillage, setSelectedVillage] = useState(null);
  const [travelProgress, setTravelProgress] = useState(null);
  const [isPressed, setIsPressed] = useState(false);
  const [completed, setCompleted] = useState(false);
  const { setError, clearError } = usePopupMessage();
  const navigate = useNavigate();
  const username = localStorage.getItem('username');
  const pressIntervalRef = useRef(null);
  const isProcessingStepRef = useRef(false);

  const normalizeVillageImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '';
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/')) return url;
    return `/${url}`;
  };

  const fetchTravelData = async () => {
    try {
      const villagesResponse = await axios.get(
        `/api/villages?username=${encodeURIComponent(username)}`
      );
      if (villagesResponse.data.success) {
        setVillages(villagesResponse.data.villages);
        
        const travelResponse = await axios.get(
          `/api/travel/progress?username=${username}`
        );
        
        if (travelResponse.data.success && travelResponse.data.hasTravel) {
          setTravelProgress(travelResponse.data.travel);
          const targetVillage = villagesResponse.data.villages.find(
            v => v.id === travelResponse.data.travel.toVillageId
          );
          if (targetVillage) {
            setSelectedVillage(targetVillage);
          }
        }
      }
    } catch (err) {
      setError(tg('travelLoadFailed'));
    }
  };

  useEffect(() => {
    if (!username) {
      navigate('/login');
      return;
    }
    fetchTravelData();
  }, [username, navigate]);

  const handleVillageSelect = async (village) => {
    if (!userCharacter) return;

    if (!village.canTravel) {
      setError(village.travelHint || tg('travelCannotTravel'));
      return;
    }

    clearError();
    
    try {
      const response = await axios.post('/api/travel/start', {
        username,
        toVillageId: village.id,
      });

      if (response.data.success) {
        setSelectedVillage(village);
        setTravelProgress(response.data.travel);
        setCompleted(false);
      } else {
        setError(response.data.message || tg('travelStartFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || tg('travelStartFailed'));
    }
  };

  const handleRunClick = async () => {
    if (!travelProgress || travelProgress.remainingSteps <= 0) return;
    
    await processStep(false);
  };

  const handleRunPress = () => {
    if (!travelProgress || travelProgress.remainingSteps <= 0 || isPressed) return;
    
    setIsPressed(true);
    
    pressIntervalRef.current = setInterval(async () => {
      await processStep(true);
    }, 100);
  };

  const handleRunRelease = () => {
    setIsPressed(false);
    if (pressIntervalRef.current) {
      clearInterval(pressIntervalRef.current);
      pressIntervalRef.current = null;
    }
  };

  const handleBackToTravelStart = async () => {
    setIsPressed(false);
    if (pressIntervalRef.current) {
      clearInterval(pressIntervalRef.current);
      pressIntervalRef.current = null;
    }

    try {
      const response = await axios.post('/api/travel/cancel', { username });
      if (!response.data?.success) {
        setError(response.data?.message || tg('travelStepFailed'));
        return;
      }
    } catch (err) {
      setError(err.response?.data?.message || tg('travelStepFailed'));
      return;
    }

    clearError();
    setSelectedVillage(null);
    setTravelProgress(null);
  };

  const processStep = async (isHolding) => {
    if (isProcessingStepRef.current) return;
    isProcessingStepRef.current = true;

    try {
      const response = await axios.post('/api/travel/step', {
        username,
        isHolding,
      });

      if (response.data.success) {
        setTravelProgress(response.data.travel);
        
        if (response.data.completed) {
          setIsPressed(false);
          if (pressIntervalRef.current) {
            clearInterval(pressIntervalRef.current);
            pressIntervalRef.current = null;
          }
          handleTravelComplete();
        }
      } else {
        setError(response.data.message || tg('travelStepFailed'));
      }
    } catch (err) {
      setError(err.response?.data?.message || tg('travelStepFailed'));
      setIsPressed(false);
      if (pressIntervalRef.current) {
        clearInterval(pressIntervalRef.current);
        pressIntervalRef.current = null;
      }
    } finally {
      isProcessingStepRef.current = false;
    }
  };

  const handleTravelComplete = async () => {
    setCompleted(true);
    await refreshCharacter().catch(() => {});
    
    setTimeout(() => {
      navigate('/game');
    }, 2000);
  };

  useEffect(() => {
    return () => {
      if (pressIntervalRef.current) {
        clearInterval(pressIntervalRef.current);
      }
    };
  }, []);

  if (completed && selectedVillage) {
    const villageName = translateVillageName(tg, selectedVillage.name);
    return (
      <div className="travel-completion-card">
        <div className="completion-message">
          <h1>{tg('travelArrived', { village: villageName })}</h1>
          {selectedVillage.imageUrl && (
            <img
              className="arrival-village-image"
              src={normalizeVillageImageUrl(selectedVillage.imageUrl)}
              alt={villageName}
            />
          )}
          <p>{tg('travelRedirecting')}</p>
        </div>
      </div>
    );
  }

  const currentVillage = villages.find((v) => Number(v.id) === Number(userCharacter?.villageId)) || null;
  const routeFromVillage = villages.find((v) => Number(v.id) === Number(travelProgress?.fromVillageId)) || currentVillage;
  const routeToVillage = selectedVillage || villages.find((v) => Number(v.id) === Number(travelProgress?.toVillageId)) || null;

  return (
    <>
        <div className="auth-selection-body travel-body">
          <Coachmark
            tourId="travel"
            enabled={!travelProgress}
            steps={[{ selector: '.travel-destination-section', textKey: 'travelDestinations' }]}
          />
          {userCharacter && (
            <section className="travel-current-section">
              <h2 className="auth-selection-section-title">{tg('travelCurrentVillage')}</h2>
              <p className="auth-info-text travel-current-village">
                <strong>{translateVillageName(tg, userCharacter.villageName)}</strong>
              </p>
            </section>
          )}

          {!travelProgress && (
            <section className="travel-destination-section">
              <h2 className="auth-selection-section-title">{tg('travelDestinations')}</h2>
              <div className="selection-grid">
                {villages
                  .filter((v) => v.id !== userCharacter?.villageId)
                  .map((village) => {
                    const locked = !village.canTravel;
                    return (
                    <div
                      key={village.id}
                      className={`selection-card ${selectedVillage?.id === village.id ? 'selected' : ''} ${locked ? 'selection-card--locked' : ''}`}
                      onClick={() => !locked && handleVillageSelect(village)}
                      onKeyDown={(e) => {
                        if (locked) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleVillageSelect(village);
                        }
                      }}
                      role="button"
                      tabIndex={locked ? -1 : 0}
                      aria-disabled={locked}
                    >
                      <div className="selection-card__media">
                        {locked ? (
                          <div className="placeholder-icon travel-village-locked-img" aria-hidden="true">?</div>
                        ) : village.imageUrl ? (
                          <img
                            src={normalizeVillageImageUrl(village.imageUrl)}
                            alt={village.name}
                          />
                        ) : (
                          <div className="placeholder-icon">🏘️</div>
                        )}
                      </div>
                      <h3>{translateVillageName(tg, village.name)}</h3>
                      <p>{village.description}</p>
                      <p className={`travel-village-meta${locked ? ' travel-village-meta--locked' : ''}`}>
                        {tg('travelRequiredLevel', { level: village.level ?? 1 })}
                        {village.distance ? ` · ${tg('travelDistance', { distance: village.distance })}` : ''}
                      </p>
                      {gelistirmeConsole && village.canTravel && village.id !== userCharacter?.villageId && (
                        <p className="travel-village-hint travel-village-hint--dev">
                          {tc('devModeMapUnlocked')}
                        </p>
                      )}
                      {village.travelHint && (
                        <p className={`travel-village-hint ${locked ? 'travel-village-hint--locked' : ''}`}>
                          {village.travelHint}
                        </p>
                      )}
                    </div>
                    );
                  })}
              </div>
            </section>
          )}

          {travelProgress && selectedVillage && (
            <section className="travel-progress">
              <button
                type="button"
                className="auth-button auth-button--compact"
                style={authButtonStyle}
                onClick={handleBackToTravelStart}
              >
                {tc('back')}
              </button>
              <h2 className="auth-selection-section-title">
                {tg('travelTarget', { village: translateVillageName(tg, selectedVillage.name) })}
              </h2>
              {routeFromVillage && routeToVillage && (
                <div className="travel-route-visual">
                  <div className="travel-route-node">
                    {routeFromVillage.imageUrl && (
                      <img
                        className="travel-route-image"
                        src={normalizeVillageImageUrl(routeFromVillage.imageUrl)}
                        alt={translateVillageName(tg, routeFromVillage.name)}
                      />
                    )}
                    <span>{translateVillageName(tg, routeFromVillage.name)}</span>
                  </div>
                  <div className="travel-route-arrow">→</div>
                  <div className="travel-route-node">
                    {routeToVillage.imageUrl && (
                      <img
                        className="travel-route-image"
                        src={normalizeVillageImageUrl(routeToVillage.imageUrl)}
                        alt={translateVillageName(tg, routeToVillage.name)}
                      />
                    )}
                    <span>{translateVillageName(tg, routeToVillage.name)}</span>
                  </div>
                </div>
              )}
              <TravelWalker
                characterId={userCharacter?.characterId}
                username={username}
                progress={travelProgress.totalSteps > 0
                  ? 1 - travelProgress.remainingSteps / travelProgress.totalSteps
                  : 0}
                running={isPressed}
                fromImg={routeFromVillage?.imageUrl ? normalizeVillageImageUrl(routeFromVillage.imageUrl) : ''}
                toImg={routeToVillage?.imageUrl ? normalizeVillageImageUrl(routeToVillage.imageUrl) : ''}
              />
              <p className="auth-info-text travel-distance-info">
                {tg('travelRemainingSteps', {
                  remaining: travelProgress.remainingSteps,
                  total: travelProgress.totalSteps,
                })}
              </p>
              
              {travelProgress.remainingSteps > 0 ? (
                <div className="run-controls">
                  <button
                    type="button"
                    className="auth-button travel-run-button"
                    style={authButtonStyle}
                    onClick={handleRunClick}
                    onMouseDown={handleRunPress}
                    onMouseUp={handleRunRelease}
                    onMouseLeave={handleRunRelease}
                    onTouchStart={handleRunPress}
                    onTouchEnd={handleRunRelease}
                  >
                    {tg('travelRun')}
                  </button>
                  <p className="auth-info-text travel-run-hint">{tg('travelRunHint')}</p>
                </div>
              ) : (
                <p className="auth-info-text travel-complete-msg">{tg('travelComplete')}</p>
              )}
            </section>
          )}
        </div>
    </>
  );
};

const Travel = () => {
  const navigate = useNavigate();
  const { t: tg } = useTranslation('gameData');

  return (
    <div className="auth-container auth-container--selection">
      <div className="auth-card" style={authCardStyle}>
        <AuthSelectionToolbar
          onBack={() => navigate('/game')}
          title={tg('travelTitle')}
          subtitle={tg('travelSubtitle')}
          buttonStyle={authButtonStyle}
        />
        <TravelPanel />
      </div>
    </div>
  );
};

export default Travel;
